import { isEscalation, isTerminal, type TicketAssignedMember, type TicketDetail } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  useAddTicketCollaborator,
  useRemoveTicketCollaborator,
  useSetTicketAssignee,
} from '../../api/ticket-assignment';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { UNASSIGNED_LABEL, personName } from '../../components/ticket';
import { AssignMemberDialog, type AssignMode } from './AssignMemberDialog';
import { ESCALATION_LABEL, REMOVED_MEMBER_LABEL, notificationText, roleAndLevel } from './assignment-text';
import { DetailSection, FactList } from './DetailSection';

/** Nome, ruolo e livello di un membro sul ticket, con l'etichetta di escalation per lo Sviluppo. */
function MemberLine({ member }: { member: TicketAssignedMember }) {
  const details = roleAndLevel(member);
  return (
    <Stack component="span" direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
      <Box component="span" sx={{ fontWeight: 500 }}>
        {personName(member)}
      </Box>
      {details && (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {details}
        </Box>
      )}
      {member.level && isEscalation(member.level) && (
        <Chip size="small" color="warning" variant="outlined" label={ESCALATION_LABEL} />
      )}
      {member.removed && <Chip size="small" variant="outlined" label={REMOVED_MEMBER_LABEL} />}
    </Stack>
  );
}

/** Esito dell'ultima modifica, legato al ticket: aprendo un altro ticket non si mostra. */
type Feedback = { code: string; severity: 'success' | 'warning'; text: string };

/** Che cosa è successo, detto in una frase, per ciascuna scelta del dialogo. */
const DONE: Record<AssignMode, (name: string) => string> = {
  assegna: (name) => `${name} è il responsabile.`,
  cambia: (name) => `${name} è il nuovo responsabile.`,
  collaboratore: (name) => `${name} collabora al ticket.`,
};

/**
 * Responsabile e collaboratori (DEC-004) con le azioni della story #4: «Assegna» (il primo
 * assegnato diventa responsabile), «Cambia responsabile», «Aggiungi collaboratore», «Rimuovi».
 * La scelta propone i membri attivi del team per livello, con il carico; un membro del secondo
 * livello è l'escalation. Dopo ogni modifica l'esito della notifica email resta visibile qui (e
 * nell'evento del thread): l'assegnazione vale anche se l'email non parte. Su un ticket Chiuso o
 * Scartato l'assegnazione non si cambia.
 */
export function TicketAssignmentSection({
  ticket,
}: {
  ticket: Pick<TicketDetail, 'code' | 'state' | 'assignee' | 'collaborators'>;
}) {
  const [mode, setMode] = useState<AssignMode | null>(null);
  const [removing, setRemoving] = useState<TicketAssignedMember | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const setAssignee = useSetTicketAssignee(ticket.code);
  const addCollaborator = useAddTicketCollaborator(ticket.code);
  const removeCollaborator = useRemoveTicketCollaborator(ticket.code);
  const assign = mode === 'collaboratore' ? addCollaborator : setAssignee;
  const editable = !isTerminal(ticket.state);
  // «Cambia responsabile» può promuovere un collaboratore (DEC-004): si esclude solo il responsabile.
  const excluded = (target: AssignMode) =>
    [
      ticket.assignee?.id,
      ...(target === 'collaboratore' ? ticket.collaborators.map((member) => member.id) : []),
    ].filter((id): id is string => id !== undefined);

  const openDialog = (next: AssignMode) => {
    setAssignee.reset();
    addCollaborator.reset();
    setMode(next);
  };

  return (
    <DetailSection title="In carico a">
      <FactList
        facts={[
          {
            label: 'Responsabile',
            value: ticket.assignee ? (
              <MemberLine member={ticket.assignee} />
            ) : (
              <Box component="span" sx={{ color: 'warning.dark', fontWeight: 500 }}>
                {UNASSIGNED_LABEL}
              </Box>
            ),
          },
          {
            label: 'Collaboratori',
            value:
              ticket.collaborators.length > 0 ? (
                <Stack component="ul" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {ticket.collaborators.map((member) => (
                    <Stack
                      component="li"
                      key={member.id}
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <MemberLine member={member} />
                      {editable && (
                        <Button
                          size="small"
                          color="inherit"
                          aria-label={`Rimuovi ${personName(member)}`}
                          // Il valore della riga va a capo ovunque (#119): il pulsante no, resta una parola intera.
                          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                          onClick={() => {
                            removeCollaborator.reset();
                            setRemoving(member);
                          }}
                        >
                          Rimuovi
                        </Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              ) : null,
            missing: 'Nessuno',
          },
        ]}
      />

      {editable ? (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {ticket.assignee ? (
            <>
              <Button size="small" variant="outlined" onClick={() => openDialog('cambia')}>
                Cambia responsabile
              </Button>
              <Button size="small" variant="outlined" onClick={() => openDialog('collaboratore')}>
                Aggiungi collaboratore
              </Button>
            </>
          ) : (
            <Button size="small" variant="contained" onClick={() => openDialog('assegna')}>
              Assegna
            </Button>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Ticket chiuso o scartato: l’assegnazione non si cambia più.
        </Typography>
      )}

      {feedback?.code === ticket.code && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
          {feedback.text}
        </Alert>
      )}

      {mode && (
        <AssignMemberDialog
          open
          mode={mode}
          code={ticket.code}
          excludedIds={excluded(mode)}
          busy={assign.isPending}
          error={assign.error?.message ?? null}
          onClose={() => setMode(null)}
          onConfirm={(member) =>
            assign.mutate(member.id, {
              onSuccess: ({ notification }) => {
                setMode(null);
                setFeedback({
                  code: ticket.code,
                  severity: notification?.outcome === 'non_inviata' ? 'warning' : 'success',
                  text: [DONE[mode](member.name), notification && notificationText(notification)]
                    .filter(Boolean)
                    .join(' '),
                });
              },
            })
          }
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Rimuovi collaboratore"
        confirmLabel="Rimuovi"
        confirmColor="error"
        busy={removeCollaborator.isPending}
        error={removeCollaborator.error?.message ?? null}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (!removing) return;
          removeCollaborator.mutate(removing.id, {
            onSuccess: () => {
              setFeedback({
                code: ticket.code,
                severity: 'success',
                text: `${personName(removing)} non collabora più al ticket.`,
              });
              setRemoving(null);
            },
          });
        }}
      >
        {removing &&
          `${personName(removing)} esce dal ticket ${ticket.code}. La rimozione resta nel thread; nessuna email.`}
      </ConfirmDialog>
    </DetailSection>
  );
}
